import * as aBNLookupStyles from './ABNLookup.styles';

import FormikError from '../FormikError';
import { addFoundTracking } from '../../utils/tracking';
import { bffActions } from '../../bff';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useState } from 'react';
import { captureException } from '@sentry/react';
import { join, map, pick, values } from 'ramda';

import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

export interface AbnDetails {
  companyName: string;
  abnState: string;
  abn: string;
}

interface ABNResultLower {
  abn: string;
  abnStatus: string;
  isCurrent: boolean;
  name: string;
  nameType: string;
  postcode: string;
  score: boolean;
  state: string;
}

export interface ABNLookupProps {
  id: string;
  value: AbnDetails;
  onBlur?: () => void;
  onChange: (value: AbnDetails) => void;
  error: boolean;
  label?: string;
  fieldId: string;
}

const abnDetails = (option: ABNResultLower): AbnDetails => {
  const suggestionFields = pick(['name', 'state', 'abn'], option);
  const joinWithComma = join(', ');
  const getAbnOptionLabel = joinWithComma(values(suggestionFields));
  return { companyName: getAbnOptionLabel, abnState: option.state, abn: option.abn };
};

function ABNLookup({ fieldId, onChange, onBlur, error, value, label, id }: ABNLookupProps) {
  const [suggestions, setSuggestions] = useState<Array<AbnDetails>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const { classes: textFieldClasses } = aBNLookupStyles.useTextFieldClasses();
  const { classes: autocompleteClasses } = aBNLookupStyles.useAutocompleteClasses();

  const handleAutocompleteChange = (_event: React.ChangeEvent<unknown>, optionValue: AbnDetails | null) =>
    optionValue && onChange(optionValue);

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const handleFetchAutocomplete = async (_event: React.ChangeEvent<unknown>, optionValue: string) => {
    if (optionValue && optionValue.length > 3) {
      setLoading(true);
      try {
        const result = await bffActions.abn.fetchAbn(optionValue);
        const resultData = result.data && result.data.data;
        if (resultData && resultData.matches) {
          const mapData = map(abnDetails, resultData.matches);
          setSuggestions(mapData);
        }
      } catch (e) {
        captureException(e);
      }

      setLoading(false);
    }
  };

  const debouncedHandleFetchAutocomplete = useCallback(debounce(handleFetchAutocomplete, 300), []);

  const handleBlurText = (_event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    addFoundTracking(
      'type',
      JSON.stringify({
        id: label || 'Search for your company',
        value: _event.target.value,
      })
    );
  };

  return (
    <Autocomplete
      id={id}
      autoComplete
      blurOnSelect
      filterSelectedOptions
      includeInputInList
      loading={loading}
      onBlur={handleBlur}
      onChange={handleAutocompleteChange}
      onInputChange={(event, valueOption) => {
        // this must be in a closure otherwise it simply does not run. Something here doesn't like
        // the voided async function `debouncedHandlefetchautocomplete` - possibly something in the
        // build system
        void debouncedHandleFetchAutocomplete(event, valueOption);
      }}
      options={[value, ...suggestions]}
      filterOptions={() => suggestions}
      value={value}
      popupIcon={<ArrowDropDownIcon />}
      classes={autocompleteClasses}
      isOptionEqualToValue={(option, optionValue) => option.abn === optionValue.abn}
      getOptionLabel={(option) => option.companyName}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <div className={textFieldClasses.root}>
          <TextField
            autoComplete="off"
            error={error}
            label={label || 'Search for your company'}
            value={value}
            {...params}
            fullWidth
            inputProps={{ ...params.inputProps, autoComplete: 'off', id }}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onBlur={handleBlurText}
          />
          <FormikError errorName={fieldId} />
        </div>
      )}
    />
  );
}

export default ABNLookup;
